import { RouteType } from "types/RouteType";
import type { RootStore } from "@intrasync/stores";
import AccountsReceivableStore from "./AccountsReceivable.Store";

/**
 * Accounts Receivable Route
 *
 * Main AR view per spec 08-accounts-receivable.md
 * - Payment from any entity (AR-01)
 * - Vendor refund receipt (AR-02)
 * - Intercompany invoicing (AR-03)
 * - Search by invoice, phone, address (AR-04)
 * - Streamlined payment entry (AR-05)
 * - Customer statements (AR-06)
 * - AR aging reports (AR-07)
 */
const accountsReceivableRoute: RouteType = {
  name: "Accounts Receivable",
  path: "/accounting/accounts-receivable",
  layout: "/app",
  viewportCard: {
    header: {
      title: "Accounts Receivable",
      enableRefreshButton: true,
      enableBackButton: true,
      color: "primary",
      component: () => import("./AccountsReceivable.Header"),
    },
    body: {
      component: () => import("./AccountsReceivable.Body"),
      grayBackground: true,
    },
  },
  group: "Accounting",
  requiresAuth: true,
  requiresAdmin: false,
  requiresPermission: false,
  enableAutoFind: false,
  showCompanySelector: true,
  beforeEnter: async ({ rootStore }: { rootStore: RootStore }) => {
    const store = (rootStore as any).accountsReceivableStore as AccountsReceivableStore;
    if (store) {
      await store.loadData();
    }
    return true;
  },
  onParamsChange: async ({ rootStore }: { rootStore: RootStore }) => {
    const store = (rootStore as any).accountsReceivableStore as AccountsReceivableStore;
    if (store) {
      await store.loadData();
    }
  },
  dialogs: [
    {
      name: "quickPayment",
      title: "Receive Payment",
      component: () => import("./dialogs/QuickPaymentDialog.Body"),
      onSubmit: async ({ rootStore }: { rootStore: RootStore }) => {
        const store = (rootStore as any).accountsReceivableStore as AccountsReceivableStore;
        await store.createPayment();
      },
      cancelButton: true,
      height: 600,
    },
    {
      name: "vendorRefund",
      title: "Vendor Refund Receipt",
      component: () => import("./dialogs/VendorRefundDialog.Body"),
      onSubmit: async ({ rootStore }: { rootStore: RootStore }) => {
        const store = (rootStore as any).accountsReceivableStore as AccountsReceivableStore;
        await store.createPayment();
      },
      cancelButton: true,
      height: 450,
    },
    {
      name: "icaInvoice",
      title: "Intercompany Invoice",
      component: () => import("./dialogs/ICAInvoiceDialog.Body"),
      onSubmit: async ({ rootStore }: { rootStore: RootStore }) => {
        const store = (rootStore as any).accountsReceivableStore as AccountsReceivableStore;
        await store.createICATransaction();
      },
      cancelButton: true,
      height: 700,
    },
    {
      name: "customerStatement",
      title: "Customer Statement",
      component: () => import("./dialogs/CustomerStatementDialog.Body"),
      cancelButton: true,
      height: 600,
    },
    {
      name: "paymentDetail",
      title: "Payment Details",
      component: () => import("./dialogs/PaymentDetailDialog.Body"),
      cancelButton: true,
      height: 450,
    },
    {
      name: "createDeposit",
      title: "Create Deposit",
      component: () => import("./dialogs/CreateDepositDialog.Body"),
      onSubmit: async ({ rootStore }: { rootStore: RootStore }) => {
        const store = (rootStore as any).accountsReceivableStore as AccountsReceivableStore;
        await store.createDeposit();
      },
      cancelButton: true,
      height: 600,
    },
    {
      name: "icaPrint",
      title: "Print ICA Invoice",
      component: () => import("./dialogs/ICAInvoicePrintDialog.Body"),
      cancelButton: true,
      height: 700,
    },
  ],
};

export default accountsReceivableRoute;
